import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import SmartFilter from './SmartFilter';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'setup_requested', label: 'Setup Requested' },
  { value: 'files_selected', label: 'Files Selected' },
  { value: 'files_requested', label: 'Files Requested' },
  { value: 'partially_received', label: 'Partially Received' },
  { value: 'all_received', label: 'All Received' },
  { value: 'in_review', label: 'In Review' },
  { value: 'review_complete', label: 'Review Complete' },
  { value: 'completed', label: 'Completed' },
  { value: 'paused', label: 'Paused' },
  { value: 'unresponsive', label: 'Unresponsive' },
];

const MONTH_OPTIONS = [
  { value: '', label: 'All Months' },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: new Date(2000, i).toLocaleString('default', { month: 'long' }),
  })),
];

const QUICK_FILTERS = [
  { key: 'no_contact', label: 'No Contact' },
  { key: 'no_units', label: 'No Units' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'attention', label: 'Attention' },
];

const AuditFilterBar = ({ filters, onFilterChange, teamMembers = [] }) => {
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [searchInput, setSearchInput] = useState(filters.search || '');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) onFilterChange({ ...filters, search: searchInput });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleSmartFilter = (aiFilters) => {
    if (Object.keys(aiFilters).length === 0) {
      onFilterChange({ search: '', status: '', assigned_to_id: '', audit_month: '', quick: '' });
      setSearchInput('');
      return;
    }
    const f = { ...filters };
    if (aiFilters.search) { f.search = aiFilters.search; setSearchInput(aiFilters.search); }
    if (aiFilters.status) f.status = aiFilters.status;
    if (aiFilters.audit_month) f.audit_month = String(aiFilters.audit_month);
    if (aiFilters.quick) f.quick = aiFilters.quick;
    if (aiFilters.state) f.state = aiFilters.state;
    if (aiFilters.assigned_to) f.assigned_to = aiFilters.assigned_to;
    if (aiFilters.has_files !== undefined) f.has_files = String(aiFilters.has_files);
    if (aiFilters.min_files_required) f.min_files_required = String(aiFilters.min_files_required);
    onFilterChange(f);
  };

  const selectClass = `px-2 py-1.5 rounded-lg border text-[12px] font-rasa ${
    d ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-600'
  } focus:outline-none focus:ring-1 focus:ring-blue-500/50`;

  const pillClass = (active) => `px-2 py-1 rounded text-[11px] font-rasa font-medium transition-colors ${
    active
      ? (d ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
      : (d ? 'bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50')
  }`;

  return (
    <div className="space-y-2">
      {/* Row 1: Smart filter */}
      <SmartFilter onApplyFilters={handleSmartFilter} />

      {/* Row 2: Quick pills + search + dropdowns — all one line */}
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Quick pills */}
        {QUICK_FILTERS.map((qf) => (
          <button
            key={qf.key}
            onClick={() => onFilterChange({ ...filters, quick: filters.quick === qf.key ? '' : qf.key })}
            className={pillClass(filters.quick === qf.key)}
          >
            {qf.label}
          </button>
        ))}
        {filters.quick && (
          <button onClick={() => onFilterChange({ ...filters, quick: '' })} className={`text-[10px] font-rasa ${d ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
            Clear
          </button>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Search */}
        <div className="relative w-48">
          <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${d ? 'text-gray-500' : 'text-gray-400'}`} />
          <input
            type="text"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={`w-full pl-7 pr-2 py-1.5 rounded-lg border text-[12px] font-rasa ${
              d ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'
            } focus:outline-none focus:ring-1 focus:ring-blue-500/50`}
          />
        </div>

        {/* Company (child company within an umbrella audit) */}
        <input
          type="text"
          placeholder="Company..."
          defaultValue={filters.processor_company || ''}
          onKeyDown={(e) => { if (e.key === 'Enter') onFilterChange({ ...filters, processor_company: e.target.value }); }}
          onBlur={(e) => onFilterChange({ ...filters, processor_company: e.target.value })}
          className={`w-36 px-2 py-1.5 rounded-lg border text-[12px] font-rasa ${
            d ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'
          } focus:outline-none focus:ring-1 focus:ring-blue-500/50`}
        />

        {/* Dropdowns */}
        <select value={filters.status || ''} onChange={(e) => onFilterChange({ ...filters, status: e.target.value })} className={selectClass}>
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <select value={filters.assigned_to_id || ''} onChange={(e) => onFilterChange({ ...filters, assigned_to_id: e.target.value })} className={selectClass}>
          <option value="">All Members</option>
          <option value="unassigned">Unassigned</option>
          {teamMembers.map((m) => <option key={m.id} value={m.user_id}>{m.username || m.email}</option>)}
        </select>

        <select value={filters.audit_month || ''} onChange={(e) => onFilterChange({ ...filters, audit_month: e.target.value })} className={selectClass}>
          {MONTH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    </div>
  );
};

export default AuditFilterBar;
